<template>
  <div
    class="history-sidebar-root"
    :class="{ 'history-sidebar-root--overlay': overlay }"
  >
    <!-- Backdrop — only rendered in overlay mode -->
    <div
      v-if="overlay && isOpen"
      class="history-backdrop"
      @click="$emit('close')"
    ></div>

    <aside
      class="history-sidebar"
      :class="{ 'history-sidebar--open': isOpen }"
      :aria-hidden="!isOpen"
    >
      <div class="history-sidebar-inner">
        <!-- Header -->
        <div class="sidebar-header">
          <h2 class="sidebar-title">
            <i class="fas fa-comments sidebar-title-icon"></i>
            <span>Chat History</span>
          </h2>
          <button class="close-button" @click="$emit('close')" title="Collapse">
            <i class="fas fa-chevron-left"></i>
          </button>
        </div>

        <!-- New Conversation Button -->
        <div class="new-conversation-section">
          <button class="new-conversation-button" @click="$emit('new-chat')">
            <i class="fas fa-plus"></i>
            <span>New Conversation</span>
          </button>
        </div>

        <!-- Search Bar -->
        <div class="search-conversation-section mb-3">
          <search-bar
            v-model="searchQuery"
            placeholder="Search By Title..."
            @search="handleSearch"
          />
        </div>

        <!-- Chat List -->
        <div class="sidebar-content">
          <!-- Loading State -->
          <div v-if="chatsLoading && chats.length === 0" class="loading-state">
            <i class="fas fa-circle-notch fa-spin"></i>
            <p>Loading chats...</p>
          </div>

          <!-- Chat Groups -->
          <div v-else-if="chats.length > 0" class="chat-groups">
            <!-- Today -->
            <div v-if="todayChats.length > 0" class="chat-group">
              <div class="group-label">Today</div>
              <div class="chat-list">
                <chat-history-item
                  v-for="chat in todayChats"
                  :key="chat.id"
                  :chat="chat"
                  :show-actions="true"
                  :selected-chat-id="selectedChat?.id"
                  :has-unseen="hasUnseenCompletion(chat.id)"
                  :is-working="isChatStreaming(chat.id)"
                  @select="handleSelectChat"
                  @delete="handleDeleteChat"
                />
              </div>
            </div>

            <!-- Yesterday -->
            <div v-if="yesterdayChats.length > 0" class="chat-group">
              <div class="group-label">Yesterday</div>
              <div class="chat-list">
                <chat-history-item
                  v-for="chat in yesterdayChats"
                  :key="chat.id"
                  :chat="chat"
                  :show-actions="true"
                  :selected-chat-id="selectedChat?.id"
                  :has-unseen="hasUnseenCompletion(chat.id)"
                  :is-working="isChatStreaming(chat.id)"
                  @select="handleSelectChat"
                  @delete="handleDeleteChat"
                />
              </div>
            </div>

            <!-- This Week -->
            <div v-if="thisWeekChats.length > 0" class="chat-group">
              <div class="group-label">This Week</div>
              <div class="chat-list">
                <chat-history-item
                  v-for="chat in thisWeekChats"
                  :key="chat.id"
                  :chat="chat"
                  :show-actions="true"
                  :selected-chat-id="selectedChat?.id"
                  :has-unseen="hasUnseenCompletion(chat.id)"
                  :is-working="isChatStreaming(chat.id)"
                  @select="handleSelectChat"
                  @delete="handleDeleteChat"
                />
              </div>
            </div>

            <!-- Older -->
            <div v-if="olderChats.length > 0" class="chat-group">
              <div class="group-label">Older</div>
              <div class="chat-list">
                <chat-history-item
                  v-for="chat in olderChats"
                  :key="chat.id"
                  :chat="chat"
                  :show-actions="true"
                  :selected-chat-id="selectedChat?.id"
                  :has-unseen="hasUnseenCompletion(chat.id)"
                  :is-working="isChatStreaming(chat.id)"
                  @select="handleSelectChat"
                  @delete="handleDeleteChat"
                />
              </div>
            </div>

            <!-- Load More -->
            <div v-if="hasMoreChats" class="load-more-section">
              <button
                class="load-more-button"
                @click="loadMoreChats"
                :disabled="chatsLoading"
              >
                <i v-if="chatsLoading" class="fas fa-circle-notch fa-spin"></i>
                <span v-else>Load More</span>
              </button>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="empty-state">
            <i class="fas fa-comments"></i>
            <p>No chats found</p>
            <small v-if="searchQuery">Try a different search term</small>
          </div>
        </div>
      </div>
    </aside>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import { debounce } from "lodash";
import Swal from "sweetalert2";
import ChatHistoryItem from "@/components/OliverBotV2/shared/ChatHistoryItem.vue";
import { groupItemsByDate } from "@/utils/dateGrouping.js";

export default {
  name: "HistorySidebar",
  components: {
    ChatHistoryItem,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    overlay: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: "",
    };
  },
  computed: {
    ...mapState({
      chats: (state) => state.oliverChatBotV2.chatHistory.pagination.data,
      chatsLoading: (state) => state.oliverChatBotV2.chatHistory.loading,
      currentPage: (state) => state.oliverChatBotV2.chatHistory.pagination.page,
      hasMoreChats: (state) =>
        state.oliverChatBotV2.chatHistory.pagination.hasMore,
      selectedChat: (state) => state.oliverChatBotV2.selectedChat,
    }),
    ...mapGetters({
      hasUnseenCompletion: "oliverChatBotV2/hasUnseenCompletion",
      isChatStreaming: "oliverChatBotV2/isChatStreaming",
    }),
    groupedChats() {
      return groupItemsByDate(this.chats, "created_at");
    },
    todayChats() {
      return this.groupedChats.today;
    },
    yesterdayChats() {
      return this.groupedChats.yesterday;
    },
    thisWeekChats() {
      return this.groupedChats.thisWeek;
    },
    olderChats() {
      return this.groupedChats.older;
    },
  },
  watch: {
    async isOpen(newVal) {
      if (newVal) {
        await this.loadChats();
        this.scrollToSelectedChat();
      } else {
        // Reset search when closing
        this.searchQuery = "";
      }
    },
  },
  created() {
    // debounced search helper captures component `this`
    this.debouncedLoadChats = debounce((query) => {
      this.loadChats(1, query);
    }, 300);
  },
  methods: {
    ...mapActions("oliverChatBotV2", ["getChats", "deleteChat"]),
    async loadChats(page = 1, searchQuery = "") {
      this.searchQuery = searchQuery;
      const params = { limit: 50, page };
      if (searchQuery) {
        params.title = searchQuery;
      }
      await this.getChats(params);
    },
    scrollToSelectedChat() {
      this.$nextTick(() => {
        const selectedElement = this.$el.querySelector(
          ".chat-history-item--selected"
        );
        if (selectedElement) {
          selectedElement.scrollIntoView({
            block: "center",
            behavior: "auto",
          });
        }
      });
    },
    async loadMoreChats() {
      await this.loadChats(this.currentPage + 1);
    },
    handleSearch(value) {
      if (this.debouncedLoadChats) {
        this.debouncedLoadChats(value);
      } else {
        this.loadChats(1, value);
      }
    },
    handleSelectChat(chat) {
      this.$emit("select-chat", chat);
    },
    async handleDeleteChat(chat) {
      const result = await Swal.fire({
        title: "Delete Chat?",
        text: `Are you sure you want to delete "${chat.title || "this chat"}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#e53e3e",
        cancelButtonColor: "#a0aec0",
        confirmButtonText: "Yes, delete it",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        const result = await this.deleteChat(chat.id);
        if (result) {
          Swal.fire({
            title: "Deleted!",
            text: "Chat has been deleted.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      }
    },
  },
  beforeUnmount() {
    if (this.debouncedLoadChats && this.debouncedLoadChats.cancel) {
      this.debouncedLoadChats.cancel();
    }
  },
};
</script>

<style lang="scss" scoped>
// ─── Root wrapper ────────────────────────────────────────────────────────────
// Push mode: display:contents makes this div invisible to flex/grid layout,
// so the <aside> inside participates in .chat-body's flex row directly.
// Overlay mode: fills the entire chat-body so the sidebar can float on top.
.history-sidebar-root {
  display: contents;

  &--overlay {
    display: block;
    position: absolute;
    inset: 0;
    z-index: 100;
    pointer-events: none;

    > * {
      pointer-events: all;
    }
  }
}

// ─── Backdrop (overlay mode only) ────────────────────────────────────────────
.history-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(2px);
  cursor: pointer;
}

// ─── Sidebar panel — push mode ───────────────────────────────────────────────
.history-sidebar {
  flex-shrink: 0;
  width: 0;
  height: 100%;
  overflow: hidden;
  opacity: 0;
  background: var(--oliver-gradient, rgba(255, 255, 255, 0.97));
  border-right: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.06));
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;

  &--open {
    box-shadow: 4px 0 20px rgba(0, 0, 0, 0.08);
    width: 25%;
    opacity: 1;

    @media (max-width: 970px) {
      min-width: 240px;
    }
  }

  // ─── Sidebar panel — overlay mode ──────────────────────────────────────────
  .history-sidebar-root--overlay & {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: clamp(240px, calc(100% - 56px), 360px);
    opacity: 1;
    overflow: visible;
    border-right: none;
    box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
    transform: translateX(-100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &.history-sidebar--open {
      transform: translateX(0);
    }
  }
}

// Narrow Oliver: larger type and taller rows for a fingertip; the search box stays
// at 16px so iOS does not zoom the page when it is focused.
.history-sidebar-root--overlay {
  .sidebar-title,
  .sidebar-title-icon {
    font-size: 16px;
  }

  .new-conversation-button {
    padding: 12px 16px;
    font-size: 15px;
  }

  .group-label {
    font-size: 11px;
  }

  :deep(.search-input) {
    font-size: 16px;
  }

  :deep(.chat-history-item) {
    min-height: 56px;
  }

  :deep(.chat-icon) {
    font-size: 15px;
  }

  :deep(.chat-title) {
    font-size: 15px;
  }

  :deep(.chat-timestamp) {
    font-size: 12.5px;
  }
}

.history-sidebar-inner {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.06));
}

.sidebar-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--oliver-text);
  margin: 0;
  letter-spacing: 0.1px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.sidebar-title-icon {
  font-size: 14px;
  color: var(--oliver-accent);
}

.close-button {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: transparent;
  border: none;
  color: var(--oliver-text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: var(--oliver-btn-subtle-hover, rgba(0, 0, 0, 0.1));
    color: var(--oliver-text);
  }

  i {
    font-size: 14px;
  }
}

.search-conversation-section {
  padding: 0px 16px 12px 16px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.06));
}

.new-conversation-section {
  padding: 12px 16px;
  flex-shrink: 0;
}

.new-conversation-button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 12px;
  background: var(--oliver-accent);
  border: none;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px var(--oliver-accent-border, rgba(0, 0, 0, 0.2));
  }

  &:active {
    transform: translateY(0);
  }

  i {
    font-size: 16px;
    flex-shrink: 0;
  }
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
  scrollbar-width: thin;
  scrollbar-color: var(--oliver-scrollbar, rgba(0, 0, 0, 0.2)) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--oliver-scrollbar, rgba(0, 0, 0, 0.2));
    border-radius: 2px;

    &:hover {
      opacity: 0.7;
    }
  }
}

.chat-groups {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.chat-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.group-label {
  font-size: 10px;
  font-weight: 600;
  color: var(--oliver-text-muted);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  padding: 0 4px;
}

.chat-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.load-more-section {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}

.load-more-button {
  padding: 8px 20px;
  border-radius: 8px;
  background: var(--oliver-accent);
  color: #fff;
  border: none;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px var(--oliver-accent-border, rgba(0, 0, 0, 0.2));
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  i {
    font-size: 13px;
  }
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  text-align: center;
  color: var(--oliver-text-muted);

  i {
    font-size: 28px;
    margin-bottom: 12px;
  }

  p {
    font-size: 14px;
    font-weight: 500;
    margin: 0 0 6px 0;
  }

  small {
    font-size: 12px;
    color: var(--oliver-text-muted);
  }
}
</style>
